import { getFrontendCustomField } from "../frontendCustomFields"
import { isFunnelFoxPipeline } from "../funnelFox"
import { debounce, getBasicOffers, getVariantPageUrl, hasOfferChance } from "../helpers"

import * as pipeLoadingOverlay from "../pipeLoadingOverlay"

// Due to reasons how product collects analytics, the exit intent functionality is done
// via the roundtrip to a separate "exit intent" page with its own url without UI,
// and then back to the actual page, where discount is applied.
export default ({ exitIntentPagePath, discountPagePath, disabled, isPaymentsView }) => ({
  init() {
    this.store = Alpine.store("data")

    // If the discountPagePath provided - this is the exit intent page itself
    // and the action should be applied.
    if (discountPagePath) {
      this.redirectFromExitIntent()
      return
    }

    if (this.isExitIntentAvailable()) {
      this.listenMouseOutExitIntent()

      // Some triggers should be disabled on payments views
      // to not interrupt the flow
      if (!isPaymentsView) {
        this.listenScrollExitIntent()

        this.listenTimerExitIntent()
      }
    }
  },

  isExitIntentAvailable() {
    const searchParams = new URLSearchParams(window.location.search)
    const getError = searchParams.get("error")
    const isInsufficient = getError === 'insufficient'
    // Listeners may be disabled for certain pages
    // while other methods from this cmp are still needed
    if (disabled || isInsufficient) return false

    // Exit intent is already applied
    if (this.store.values.exitIntent) return false

    // Avoid page reload when closing exit intent popup
    if (this.store.values.isExitIntentPopupShown) return false

    // Disable exit intent mechanism for multi-month pipes if offers not configured
    const basicOffers = getBasicOffers(pipelineConfig.offers)
    const isMultiMonth = basicOffers.length > 1

    if (isMultiMonth) {
      const isDiscountConfigured = basicOffers.some(hasOfferChance)

      if (!isDiscountConfigured) {
        return false
      }
    }

    // FunnelFox pipe doesn't support exit-intent
    if (isFunnelFoxPipeline() && !this.store.values.ffExitIntentPath) {
      return false
    }

    return true
  },

  // Init "mouseout" event listener
  listenMouseOutExitIntent() {
    window.addEventListener("mouseout", (e) => {
      if (e.clientY < 0 && !this.store.values.isPaymentLoading) {
        window.location = getVariantPageUrl({ pagePath: exitIntentPagePath, pipelineConfig })
      }
    })
  },

  // Init "scroll" event listener
  listenScrollExitIntent() {
    const exitIntentScrollTrigger = getFrontendCustomField({
      pipelineConfig,
      fieldName: "exitIntentScrollTrigger",
    })

    // Disable
    if (!exitIntentScrollTrigger) return

    const handlerScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight
      const contentHeight = document.documentElement.scrollHeight

      const scrollRatio = scrollPosition / contentHeight

      if (scrollRatio >= 0.9) {
        window.location = getVariantPageUrl({ pagePath: exitIntentPagePath, pipelineConfig })
      }
    }

    window.addEventListener("scroll", debounce(handlerScroll, 150))
  },

  // Init "scroll" event listener
  listenTimerExitIntent() {
    const DEFAULT_TIMEOUT = 15000
    const exitIntentTimerTrigger = getFrontendCustomField({
      pipelineConfig,
      fieldName: "exitIntentTimerTrigger",
    })
    let timeout = DEFAULT_TIMEOUT

    // Disable
    if (!exitIntentTimerTrigger && exitIntentTimerTrigger !== 0) return

    // The given parameter expected to be a number
    // but if somebody sets is as wrong string, including 'true', which
    // meant to just 'turn the setting on' - then use the default value.
    // Zero also leads to default value due to requirements.
    const isNotInteger = !!exitIntentTimerTrigger && !Number.isInteger(exitIntentTimerTrigger)
    const isZero = exitIntentTimerTrigger === 0
    if (isNotInteger || isZero) {
      timeout = DEFAULT_TIMEOUT
    } else {
      timeout = exitIntentTimerTrigger * 1000
    }

    setTimeout(() => {
      window.location = getVariantPageUrl({ pagePath: exitIntentPagePath, pipelineConfig })
    }, timeout)
  },

  // Manually trigger exit intent - needed for touchscreens where we can't track "mouseout".
  // Accepts the callback to call if exit intent has been already applied to not prevent
  // the original flow
  handleBackButtonExitIntent(originalFn) {
    this.store.internalTracker.setEventToSessionStorage({ clickName: "Back Clicked" })

    // Show loading UI
    pipeLoadingOverlay.show()

    if (this.isExitIntentAvailable() && !this.store.values.isPaymentLoading) {
      window.location = getVariantPageUrl({ pagePath: exitIntentPagePath, pipelineConfig })
    } else {
      if (originalFn) {
        originalFn()
      } else {
        if (isFunnelFoxPipeline()) {
          const ffOrigin = this.store.values.ffOrigin
          const ffOriginPath = this.store.values.ffOriginPath
          const ffExitIntentPath = this.store.values.ffExitIntentPath
          const ffSessionId = this.store.values.ffSessionId

          if (ffOrigin && ffExitIntentPath) {
            window.location.replace(
              `${ffOrigin}/${ffOriginPath}/${ffExitIntentPath}/?fsid=${ffSessionId}`,
            )
          } else {
            throw new Error("[FunnelFox] Missing ff-origin or ff-exit-intent-path")
          }
        } else {
          history.back()
        }

        const currentPath = window.location.pathname
        let isNavigatedAway = false

        // Stop the loading UI after some delay if history.back didn't work
        // for the user (we observe some rare cases with the such issue)
        const fallbackTimeout = setTimeout(() => {
          if (!isNavigatedAway && window.location.pathname === currentPath) {
            pipeLoadingOverlay.hide()
          }
        }, 5000)

        // Detect is user has been navigated from the current page
        // to cancel the loading UI fallback
        window.addEventListener("pagehide", () => {
          isNavigatedAway = true
          clearTimeout(fallbackTimeout)
          pipeLoadingOverlay.hide()
        })
      }
    }
  },

  // Triggered on separate "exit intent" page with its own url without UI
  redirectFromExitIntent() {
    // Set flag to show discount UI on the page we redirect to
    this.store.values.isExitIntentPopupShown = true

    // The `exitIntent` name kept for historical reasons
    // and it marks that discount is applied.
    // Set it right now, to let the new price be determined properly
    // during discount page init, which recreates checkout session
    this.store.values.exitIntent = true

    // Redirect to the page, where discount UI is shown

    window.location = getVariantPageUrl({ pagePath: discountPagePath, pipelineConfig })
  },

  applyExitIntentDiscount() {
    this.store.internalTracker.trackClick({ clickName: "Get Discount Selected" })

    this.store.values.isExitIntentPopupShown = false
  },

  isExitIntentPopupShown() {
    return this.store.values.isExitIntentPopupShown
  },

  isExitIntentApplied() {
    return this.store.values.exitIntent
  },

  // Not show discount UI before popup is closed
  isExitIntentUiApplied() {
    return this.isExitIntentApplied() && !this.isExitIntentPopupShown()
  },

  getExitIntentDiscount() {
    return this.store.pricing.getPriceDiscount({
      offer: this.store.values.activeOffer,
      isChance: this.store.values.exitIntent,
    })
  },
})
